import { useEffect, useState } from "react";

/**
 * Ingram auto-fulfilment mapping for ONE plan (pricing row), managed directly
 * by the admin. Upserts the plan's Ingram identifiers into ingram_product_maps
 * (keyed by price_id) via /admin/set_ingram_mapping. Only meaningful for a LIVE
 * plan (numeric price_id); the caller hides it for new/unsaved plans.
 */

const REACT_APP_SERVER_URL = process.env.REACT_APP_SERVER_URL;

const EMPTY = {
  provider: "microsoft",
  purchase_flow: "api",
  ingram_plan_id: "",
  ingram_vendor_part_number: "",
  ingram_resource_id: "",
  ingram_product_id: "",
  product_url: "",
  default_purchase_type: "new",
  active: true,
  flexible: false,
};

const S = {
  card: {
    border: "1px solid #dbe4ee",
    borderRadius: 12,
    background: "#f8fafc",
    padding: 16,
    marginTop: 16,
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  title: { fontSize: 14, fontWeight: 700, color: "#14213d", margin: 0 },
  mapped: { color: "#16a34a", fontWeight: 600, marginLeft: 6 },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  label: { fontSize: 11, color: "#5f6c7b", display: "block", marginBottom: 4 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 8,
    border: "1px solid #dbe4ee",
    background: "#fff",
    padding: "8px 10px",
    fontSize: 13,
    color: "#14213d",
    outline: "none",
  },
  row: { display: "flex", alignItems: "center", gap: 12 },
  save: {
    borderRadius: 999,
    border: "none",
    background: "linear-gradient(135deg, #0f766e, #1d4ed8)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    padding: "9px 18px",
    cursor: "pointer",
  },
  msg: { fontSize: 12, color: "#5f6c7b" },
  // <label> gets styled by bootstrap globally (block, full-width, sometimes
  // justify-content: space-between via .form-check-label) which was pushing
  // the label text far to the right and wrapping it. Everything here uses
  // <div> to bypass that, with inline flex to keep checkbox + text adjacent.
  flexBlock: {
    background: "#fff",
    border: "1px solid #dbe4ee",
    borderRadius: 10,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  flexToggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 8,
  },
  flexToggleText: {
    fontSize: 13,
    fontWeight: 600,
    color: "#14213d",
    userSelect: "none",
  },
  helpText: {
    margin: 0,
    fontSize: 12,
    color: "#64748b",
    lineHeight: 1.45,
  },
};

export default function IngramMappingPanel({ priceId, skuId }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [mapped, setMapped] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setMsg("");
    (async () => {
      try {
        const res = await fetch(`${REACT_APP_SERVER_URL}/admin/get_ingram_mapping`, {
          method: "POST",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ price_id: priceId }),
        });
        const json = await res.json();
        if (!alive) return;
        if (json && json.data) {
          setForm({ ...EMPTY, ...json.data });
          setMapped(true);
        } else {
          setForm(EMPTY);
          setMapped(false);
        }
      } catch {
        if (alive) setForm(EMPTY);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [priceId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isGoogle = form.provider === "google";
  const isPortal = isGoogle && form.purchase_flow === "portal";

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      const res = await fetch(`${REACT_APP_SERVER_URL}/admin/set_ingram_mapping`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, price_id: priceId, sku_id: skuId }),
      });
      const json = await res.json();
      if (json && json.success) {
        setMapped(true);
        setMsg("Saved ✓");
      } else {
        setMsg(json?.msg || "Save failed");
      }
    } catch (e) {
      setMsg("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return <div style={{ ...S.card, color: "#5f6c7b", fontSize: 13 }}>Loading Ingram mapping…</div>;

  return (
    <div style={S.card}>
      <div style={S.head}>
        <h4 style={S.title}>
          Ingram auto-fulfilment
          {mapped && <span style={S.mapped}>· mapped</span>}
        </h4>
        <label style={S.checkbox}>
          <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />
          Active
        </label>
      </div>

      <div style={S.grid}>
        <div>
          <label style={S.label}>Provider</label>
          <select style={S.input} value={form.provider} onChange={(e) => set("provider", e.target.value)}>
            <option value="microsoft">Microsoft</option>
            <option value="google">Google</option>
          </select>
        </div>
        <div>
          <label style={S.label}>Fulfilment</label>
          <select style={S.input} value={form.purchase_flow} onChange={(e) => set("purchase_flow", e.target.value)}>
            <option value="api">API</option>
            <option value="portal">Portal bot (Google net-new & transfer)</option>
          </select>
        </div>
      </div>

      {isPortal ? (
        <div>
          <label style={S.label}>Google product URL (subscriptionoptions)</label>
          <input
            style={S.input}
            value={form.product_url}
            onChange={(e) => set("product_url", e.target.value)}
            placeholder="https://in.ingrammicro.com/cep/app/product/subscriptionoptions?..."
          />
        </div>
      ) : (
        <div style={S.grid}>
          <div>
            <label style={S.label}>Plan ID</label>
            <input style={S.input} value={form.ingram_plan_id} onChange={(e) => set("ingram_plan_id", e.target.value)} />
          </div>
          {!isGoogle && (
            <div>
              <label style={S.label}>Vendor Part Number (VPN)</label>
              <input
                style={S.input}
                value={form.ingram_vendor_part_number}
                onChange={(e) => set("ingram_vendor_part_number", e.target.value)}
              />
            </div>
          )}
          <div>
            <label style={S.label}>Resource ID</label>
            <input
              style={S.input}
              value={form.ingram_resource_id}
              onChange={(e) => set("ingram_resource_id", e.target.value)}
            />
          </div>
          <div>
            <label style={S.label}>Product ID</label>
            <input
              style={S.input}
              value={form.ingram_product_id}
              onChange={(e) => set("ingram_product_id", e.target.value)}
            />
          </div>
        </div>
      )}

      {isGoogle && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={S.flexBlock}>
            <div
              style={{ ...S.flexToggleRow, cursor: "pointer" }}
              onClick={() => set("flexible", !form.flexible)}
              role="checkbox"
              aria-checked={!!form.flexible}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  set("flexible", !form.flexible);
                }
              }}
            >
              <input
                type="checkbox"
                checked={!!form.flexible}
                onChange={(e) => set("flexible", e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                style={{ margin: 0, width: 16, height: 16, cursor: "pointer" }}
              />
              <span style={S.flexToggleText}>Flexible (usage-based) plan</span>
            </div>
            <p style={S.helpText}>
              Tick for the Google <b>“Business Starter Flexible (Usage Based)”</b> plan.
              Licence changes on this price are billed on our Stripe only (no Ingram
              provisioning) and run through the capping flow — when the customer's Google
              Workspace isn't connected, the team is emailed to raise the Ingram cap
              before billing.
            </p>
          </div>
          <div>
            <div style={S.label}>Google order type</div>
            <select
              style={S.input}
              value={form.default_purchase_type}
              onChange={(e) => set("default_purchase_type", e.target.value)}
            >
              <option value="new">Net new — create a new Google account</option>
              <option value="transfer">Transfer — move an existing Google account to us</option>
              <option value="existent">Add to existing — add a service to a Google account already with us</option>
            </select>
            <p style={{ ...S.helpText, marginTop: 6 }}>
              Net new & Transfer use the portal flow — set <b>Purchase flow = portal</b> and
              provide the <b>product URL</b> above. Net new asks the customer for an admin
              username; Transfer shows the customer the reseller code + steps and collects
              a transfer token. <b>Add to existing</b> is not wired through Ingram yet (it
              stays in the normal order flow).
            </p>
          </div>
        </div>
      )}

      <div style={S.row}>
        <button type="button" disabled={saving} onClick={save} style={{ ...S.save, opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : "Save mapping"}
        </button>
        {msg && <span style={S.msg}>{msg}</span>}
      </div>
    </div>
  );
}
